import axios from "axios";
import { API_BASE_URL, REFRESH_TOKEN_ENDPOINT } from "../config.js";

// ──────────────────────────────────────────────────────────────────────────
// Storage helpers (single source of truth for auth tokens)
// ──────────────────────────────────────────────────────────────────────────
export const TOKEN_KEY = "authToken";
export const REFRESH_KEY = "refreshToken";
export const ROLE_KEY = "userRole";

const storage = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  setAccess: (t) => localStorage.setItem(TOKEN_KEY, t),
  setRefresh: (t) => localStorage.setItem(REFRESH_KEY, t),
  setRole: (r) => localStorage.setItem(ROLE_KEY, r),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ROLE_KEY);
  },
};

// Custom event so the rest of the app can react to a forced logout
export const SESSION_EXPIRED_EVENT = "gh:session-expired";

function emitSessionExpired(reason = "refresh-failed") {
  try {
    window.dispatchEvent(
      new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } })
    );
  } catch (e) {
    /* SSR / older browsers — no-op */
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Axios instance — used by ~95% of API helpers
// ──────────────────────────────────────────────────────────────────────────
const http = axios.create({
  baseURL: API_BASE_URL,
});

// 1) Inject access token on every request
http.interceptors.request.use(
  (config) => {
    const token = storage.getAccess();
    if (token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 2) Singleton refresh promise — every concurrent 401 shares the same refresh call
let refreshInFlight = null;

async function performRefresh() {
  const refreshTokenValue = storage.getRefresh();
  if (!refreshTokenValue) {
    throw new Error("No refresh token available");
  }

  // Use a *bare* axios call so this never recurses through our interceptor
  const response = await axios.post(`${API_BASE_URL}${REFRESH_TOKEN_ENDPOINT}`, {
    refresh: refreshTokenValue,
  });

  const data = response.data || {};
  const newAccess = data.access || data.accessToken;
  const newRefresh = data.refresh || data.refreshToken;

  if (!newAccess) {
    throw new Error("Refresh endpoint did not return an access token");
  }

  storage.setAccess(newAccess);
  if (newRefresh) storage.setRefresh(newRefresh);

  return newAccess;
}

function startRefresh() {
  // If a refresh is already underway, just reuse its resolved value
  if (!refreshInFlight) {
    refreshInFlight = performRefresh()
      .catch((err) => {
        // Refresh failed → force logout
        storage.clear();
        emitSessionExpired("refresh-failed");
        return null;
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

// Anchored regex: only flag the refresh endpoint itself, not paths that just *mention* it.
const REFRESH_PATH_RE = /\/auth\/(api\/v\d+\/token\/refresh|refresh)\/?$/i;

function isRefreshRequest(config) {
  const url = config?.url || "";
  return REFRESH_PATH_RE.test(url);
}

// 3) Response interceptor — auto-retry the original request after refresh
http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    if (!status || !originalRequest) {
      return Promise.reject(error);
    }

    // Don't loop on the refresh endpoint or on already-retried requests
    if (status !== 401 || originalRequest._retry || isRefreshRequest(originalRequest)) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    const newToken = await startRefresh();
    if (!newToken) {
      return Promise.reject(error);
    }

    // Retry via the SAME instance so baseURL + future interceptors apply
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
    return http.request(originalRequest);
  }
);

// ──────────────────────────────────────────────────────────────────────────
// Tiny helper API (axios-based callers)
// ──────────────────────────────────────────────────────────────────────────
const api = {
  get: (url, config = {}) => http.get(url, config),
  post: (url, data, config = {}) => http.post(url, data, config),
  put: (url, data, config = {}) => http.put(url, data, config),
  patch: (url, data, config = {}) => http.patch(url, data, config),
  delete: (url, config = {}) => http.delete(url, config),
  request: (config) => http.request(config),
  instance: http,
};

export { storage as tokenStorage };
export default api;

// ──────────────────────────────────────────────────────────────────────────
// authenticatedFetch — same refresh logic, for the legacy `fetch` callers
// in CustomerSupport/authHttp.js + Setting/http.js. Keeps the response shape
// compatible (caller still calls .json()/ok/etc. as before).
// ──────────────────────────────────────────────────────────────────────────

function buildHeaders(headers = {}) {
  const token = storage.getAccess();
  return {
    Accept: "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };
}

function buildBody(data) {
  if (data == null) return undefined;
  if (data instanceof FormData) return data;
  if (typeof data === "string") return data;
  return JSON.stringify(data);
}

async function rawFetch(url, options = {}) {
  const fullUrl = url.startsWith("http")
    ? url
    : `${API_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;

  const method = (options.method || "GET").toUpperCase();
  const headers = buildHeaders(options.headers);

  // Let the browser set the multipart boundary automatically
  if (options.body instanceof FormData) {
    delete headers["Content-Type"];
  } else if (!headers["Content-Type"] && method !== "GET" && method !== "HEAD") {
    headers["Content-Type"] = "application/json";
  }

  return fetch(fullUrl, {
    ...options,
    method,
    headers,
    body: buildBody(options.body),
  });
}

const RETRY_KEY = "_ghRetried";

export async function fetchWithAuth(url, options = {}) {
  const response = await rawFetch(url, options);

  if (response.status !== 401 || options[RETRY_KEY] || REFRESH_PATH_RE.test(url)) {
    return response;
  }

  const newToken = await startRefresh();
  if (!newToken) {
    return response; // let the caller handle the 401 + global redirect kicks in
  }

  // Retry once with the fresh token
  const retriedOptions = { ...options, [RETRY_KEY]: true };
  retriedOptions.headers = {
    ...(options.headers || {}),
    Authorization: `Bearer ${newToken}`,
  };
  return rawFetch(url, retriedOptions);
}

// JSON-friendly variant: throws on !ok just like the existing fetch callers
export async function fetchJSON(url, options = {}) {
  const res = await fetchWithAuth(url, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(
      data?.detail || data?.message || `Request failed with ${res.status}`
    );
    error.code = res.status;
    error.info = data;
    error.response = { status: res.status, data };
    throw error;
  }
  return data;
}
